<script lang="ts" context="module">
  import { writable, type Writable } from "svelte/store";

  export enum AuthType {
    "SignIn" = "sign-in",
    "SignUp" = "sign-up",
  }
  export interface LoginModalContext {
    join: (authType?: AuthType) => void;
    login: (authType?: AuthType) => void;
    open: Writable<boolean>;
  }
  export const contextKey = Symbol();
  export const getLoginContext = () => getContext<LoginModalContext>(contextKey);
</script>

<script lang="ts">
  import SignIn from "$components/auth/SignIn.svelte";
  import SignUp from "$components/auth/SignUp.svelte";
  import { community, communitySlug } from "$stores/community";
  import { joinCommunity } from "app/utils/apiHelpers";
  import Modal from "app/components/+shared/Modal/Modal.svelte";
  import { getContext, setContext } from "svelte";
  import { isUserAuthed, session } from "$stores/auth";
  import { page } from "$app/stores";
  import { goto } from "$app/navigation";
  import type { CommunitySignUpRequest } from "@outverse-video/outverse-api-client-lib";
  import { verifyEmailUrl } from "$utils/urls";

  let authType = AuthType.SignIn;
  // writable store so context children can react to open/close events
  let open = writable<boolean>(false);
  let join = false;

  // provide join function for children to join community/login flow
  setContext(contextKey, {
    join: (requestAuthType?: AuthType) => {
      authType = requestAuthType || AuthType.SignIn;
      join = true;
      if ($isUserAuthed) joinCommunity($communitySlug).then(gotoNextStep);
      else $open = true;
    },
    login: (requestAuthType?: AuthType) => {
      authType = requestAuthType || AuthType.SignIn;
      join = false;
      $open = true;
    },
    open,
  });

  /** move user along to next step or refresh the current page with new state */
  const gotoNextStep = (signUpRequest?: CommunitySignUpRequest, signupProvider?: string) => {
    $open = false;
    // user signed up with email and MUST verify it to move on
    if (signupProvider === "email") {
      goto(`${verifyEmailUrl()}/?next=${$page.url}`, {
        state: { communityMembership: signUpRequest?.status },
      });
      return;
    }
    // user just authed so refresh page to update state
    goto($page.url, {
      invalidateAll: true,
      state: { communityMembership: signUpRequest?.status },
    });
    community.fetch($communitySlug);
  };

  /** decide how to move user along flow */
  async function afterAuth(signupProvider?: string) {
    let request;
    // user requested to join so attempt to join
    if (join) {
      request = await joinCommunity($communitySlug);
    }
    // move user along flow
    gotoNextStep(request, signupProvider);
    $session.insideAuthFlow = false;
  }
</script>

<slot />

<Modal maxWidth="max-w-[400px]" bind:open={$open} backdropVariant="secondary" withCloseIcon>
  {#if authType === AuthType.SignIn}
    <SignIn
      inModal
      on:authed={() => afterAuth()}
      on:requestSignup={() => (authType = AuthType.SignUp)}
    />
  {:else if authType === AuthType.SignUp}
    <SignUp
      inModal
      joiningCommunity={join ? $communitySlug : undefined}
      on:authed={(ev) => afterAuth(ev.detail)}
      on:requestSignIn={() => (authType = AuthType.SignIn)}
    />
  {/if}
</Modal>
